// SCHEDULE DISPLAY TIME POLICY (LOCK)
// -----------------------------------
// This helper controls ONLY the /schedule consumer view. It MUST NOT be
// used by the LIVE Engine (YouTube auto-detect, poller, live_sessions,
// Comment Engine, OMS, TikTok/Instagram foundation, or the seller
// broadcast lifecycle). Real broadcast state remains driven by
// getBroadcastPhase / server-side status.
//
// Rule (KST is naturally respected because we compare epoch ms — the
// underlying JS Date already parses the ISO offset carried by every
// scheduled_at string that comes back from the API):
//
//   UPCOMING : now < scheduledAt
//   LIVE     : scheduledAt <= now < scheduledAt + 60 min
//   PAST     : now >= scheduledAt + 60 min

export const LIVE_WINDOW_MIN = 60;

/**
 * Returns 'LIVE' | 'UPCOMING' | 'PAST' | null (null when scheduledAt is
 * missing or unparseable — callers should treat null as PAST so bad rows
 * never leak into the LIVE bucket).
 * @param {string|Date} scheduledAt
 * @param {Date} [nowRef=new Date()] injectable clock for tests
 */
export function getDisplayStatus(scheduledAt, nowRef = new Date()) {
  if (!scheduledAt) return null;
  const startMs = scheduledAt instanceof Date
    ? scheduledAt.getTime()
    : Date.parse(scheduledAt);
  if (!Number.isFinite(startMs)) return null;
  const endMs = startMs + LIVE_WINDOW_MIN * 60 * 1000;
  const nowMs = nowRef.getTime();
  if (nowMs < startMs) return 'UPCOMING';
  if (nowMs < endMs) return 'LIVE';
  return 'PAST';
}

/**
 * Bucket a list of items ({scheduled_at, ...}) into
 *   live / todayUpcoming / future / past
 * using the +60min display rule.  Today boundary is midnight local time
 * (matches the KST day switch for browsers set to Asia/Seoul; users in
 * other zones see their own local midnight, which is fine because the
 * page is always titled by the selected date chip).
 */
export function bucketByDisplayStatus(items, nowRef = new Date()) {
  const startOfToday = new Date(nowRef); startOfToday.setHours(0, 0, 0, 0);
  const startOfTomorrow = new Date(startOfToday);
  startOfTomorrow.setDate(startOfTomorrow.getDate() + 1);
  const buckets = { live: [], todayUpcoming: [], future: [], past: [] };
  for (const it of items) {
    const status = getDisplayStatus(it.scheduled_at, nowRef);
    if (status === 'LIVE') buckets.live.push(it);
    else if (status === 'UPCOMING') {
      const startMs = Date.parse(it.scheduled_at);
      (startMs < startOfTomorrow.getTime() ? buckets.todayUpcoming : buckets.future).push(it);
    } else buckets.past.push(it); // PAST + null (bad data) → hidden bucket
  }
  const byTime = (a, c) => (Date.parse(a.scheduled_at || 0) - Date.parse(c.scheduled_at || 0));
  buckets.live.sort(byTime);
  buckets.todayUpcoming.sort(byTime);
  buckets.future.sort(byTime);
  // Past ascending (matches the "09시대 → 10시대" mock in the spec)
  buckets.past.sort(byTime);
  return buckets;
}

/** Group PAST items by KST-hour label (e.g. "09시대"). */
export function groupPastByHour(pastItems) {
  const groups = new Map(); // hourKey → { label, items }
  for (const it of pastItems) {
    const d = new Date(it.scheduled_at);
    if (Number.isNaN(d.getTime())) continue;
    const hh = d.getHours();
    const key = String(hh).padStart(2, '0');
    if (!groups.has(key)) {
      groups.set(key, { label: `${key}시대`, items: [] });
    }
    groups.get(key).items.push(it);
  }
  return Array.from(groups.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, v]) => v);
}
